/**
 * Markdown parsing for report content — pure, no React.
 *
 * Split from `markdown.tsx` so that file exports a component only. Everything
 * here turns text into data; nothing here produces markup, which is the property
 * that makes the renderer XSS-safe (see the note in markdown.tsx).
 */

export type Inline = { text: string; bold?: boolean; italic?: boolean; code?: boolean };

/**
 * Split one line into styled runs. Single pass over the three delimiters, so a
 * stray unmatched `*` or backtick stays literal instead of eating the rest of
 * the line.
 */
export function parseInline(line: string): Inline[] {
  const out: Inline[] = [];
  let buffer = '';
  let i = 0;

  const flush = () => {
    if (buffer) {
      out.push({ text: buffer });
      buffer = '';
    }
  };

  while (i < line.length) {
    const rest = line.slice(i);

    const code = /^`([^`]+)`/.exec(rest);
    if (code) {
      flush();
      out.push({ text: code[1], code: true });
      i += code[0].length;
      continue;
    }

    const bold = /^\*\*([^*]+)\*\*/.exec(rest);
    if (bold) {
      flush();
      out.push({ text: bold[1], bold: true });
      i += bold[0].length;
      continue;
    }

    const italic = /^\*([^*]+)\*/.exec(rest) ?? /^_([^_]+)_/.exec(rest);
    if (italic) {
      flush();
      out.push({ text: italic[1], italic: true });
      i += italic[0].length;
      continue;
    }

    buffer += line[i];
    i += 1;
  }

  flush();
  return out;
}

export type Block =
  | { kind: 'heading'; level: number; text: string }
  | { kind: 'paragraph'; lines: string[] }
  | { kind: 'ul'; items: string[] }
  | { kind: 'ol'; items: string[] }
  | { kind: 'quote'; lines: string[] }
  | { kind: 'code'; lines: string[] }
  | { kind: 'hr' };

/** Group raw lines into blocks. Exported for tests. */
export function parseBlocks(markdown: string): Block[] {
  const lines = markdown.replace(/\r\n/g, '\n').split('\n');
  const blocks: Block[] = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];
    const trimmed = line.trim();

    if (!trimmed) {
      i += 1;
      continue;
    }

    // Fenced code — consumed verbatim, never re-parsed for inline spans.
    if (/^```/.test(trimmed)) {
      const body: string[] = [];
      i += 1;
      while (i < lines.length && !/^```/.test(lines[i].trim())) {
        body.push(lines[i]);
        i += 1;
      }
      i += 1; // closing fence (or EOF)
      blocks.push({ kind: 'code', lines: body });
      continue;
    }

    if (/^(-{3,}|\*{3,}|_{3,})$/.test(trimmed)) {
      blocks.push({ kind: 'hr' });
      i += 1;
      continue;
    }

    const heading = /^(#{1,6})\s+(.*)$/.exec(trimmed);
    if (heading) {
      blocks.push({ kind: 'heading', level: heading[1].length, text: heading[2].trim() });
      i += 1;
      continue;
    }

    if (/^>\s?/.test(trimmed)) {
      const body: string[] = [];
      while (i < lines.length && /^>\s?/.test(lines[i].trim())) {
        body.push(lines[i].trim().replace(/^>\s?/, ''));
        i += 1;
      }
      blocks.push({ kind: 'quote', lines: body });
      continue;
    }

    if (/^[-*]\s+/.test(trimmed)) {
      const items: string[] = [];
      while (i < lines.length && /^[-*]\s+/.test(lines[i].trim())) {
        items.push(lines[i].trim().replace(/^[-*]\s+/, ''));
        i += 1;
      }
      blocks.push({ kind: 'ul', items });
      continue;
    }

    if (/^\d+[.)]\s+/.test(trimmed)) {
      const items: string[] = [];
      while (i < lines.length && /^\d+[.)]\s+/.test(lines[i].trim())) {
        items.push(lines[i].trim().replace(/^\d+[.)]\s+/, ''));
        i += 1;
      }
      blocks.push({ kind: 'ol', items });
      continue;
    }

    // Paragraph: run until a blank line or the start of another block.
    const body: string[] = [];
    while (i < lines.length) {
      const candidate = lines[i].trim();
      if (
        !candidate ||
        /^(#{1,6})\s+/.test(candidate) ||
        /^[-*]\s+/.test(candidate) ||
        /^\d+[.)]\s+/.test(candidate) ||
        /^>\s?/.test(candidate) ||
        /^```/.test(candidate) ||
        /^(-{3,}|\*{3,}|_{3,})$/.test(candidate)
      ) {
        break;
      }
      body.push(candidate);
      i += 1;
    }
    blocks.push({ kind: 'paragraph', lines: body });
  }

  return blocks;
}

